/**
 * Effective time: real time after the offline slowdown (docs/SPEC.md section 3). While the app is open time counts in
 * full. While it is closed ("away"), the first 4 hours count in full, hours 4 to 12 count at a quarter, and anything
 * beyond 12 hours counts in full again. There is no quiet-hours setting: the slowdown depends only on how long the app
 * has been closed.
 *
 * A session records when the app was open: from `openAt` to `lastSeenAt` (a heartbeat the client updates about once a
 * minute). An away stretch runs from one session's `lastSeenAt` to the next session's `openAt`, or to the end of time
 * after the last session. A session that crashed is simply closed at its last heartbeat.
 */

export interface Session {
  openAt: number;
  lastSeenAt: number;
}

const HOUR = 3_600_000;
const FULL_SPEED_HOURS = 4;
const SLOW_UNTIL_HOURS = 12;
const SLOW_RATE = 0.25;

/** Effective hours inside an away stretch between two offsets (in hours since the stretch started). */
function awayEffectiveHours(fromOffset: number, toOffset: number): number {
  const part = (lo: number, hi: number, rate: number) => Math.max(0, Math.min(toOffset, hi) - Math.max(fromOffset, lo)) * rate;
  return part(0, FULL_SPEED_HOURS, 1) + part(FULL_SPEED_HOURS, SLOW_UNTIL_HOURS, SLOW_RATE) + part(SLOW_UNTIL_HOURS, Infinity, 1);
}

/** Sorts the sessions and merges any that overlap, so the open time is a list of separate intervals. */
function mergeSessions(sessions: readonly Session[]): Array<[number, number]> {
  const sorted = sessions
    .filter((s) => Number.isFinite(s.openAt) && Number.isFinite(s.lastSeenAt))
    .map((s) => [s.openAt, Math.max(s.openAt, s.lastSeenAt)] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const [open, seen] of sorted) {
    const last = merged[merged.length - 1];
    if (last && open <= last[1]) last[1] = Math.max(last[1], seen);
    else merged.push([open, seen]);
  }
  return merged;
}

export interface EffectiveClock {
  /** The effective hours that pass between two real instants (milliseconds). */
  between(from: number, to: number): number;
  /** The real instant at which `targetHours` effective hours have passed since `from`. */
  timeWhen(from: number, targetHours: number): number;
}

/** Builds a clock once for a list of sessions, so many questions can be asked without re-sorting them. */
export function makeEffectiveClock(sessions: readonly Session[]): EffectiveClock {
  const open = mergeSessions(sessions);

  const between = (from: number, to: number): number => {
    if (!(to > from)) return 0;
    // With no sessions at all there is nothing to slow down: count real time.
    if (open.length === 0) return (to - from) / HOUR;

    let total = 0;
    const addOpen = (start: number, end: number) => {
      const lo = Math.max(from, start);
      const hi = Math.min(to, end);
      if (hi > lo) total += (hi - lo) / HOUR;
    };
    const addAway = (start: number, end: number) => {
      const lo = Math.max(from, start);
      const hi = Math.min(to, end);
      if (hi > lo) total += awayEffectiveHours((lo - start) / HOUR, (hi - start) / HOUR);
    };

    // Before the first session the pet has not hatched yet; count real time (the hatch is the first open).
    addOpen(-Infinity, (open[0] as [number, number])[0]);
    open.forEach(([start, end], i) => {
      addOpen(start, end);
      const next = open[i + 1];
      addAway(end, next ? next[0] : Infinity);
    });
    return total;
  };

  // Effective time never decreases and passes at least a quarter as fast as real time, so bisection finds the
  // unique answer, which is at most 4x the target away.
  const timeWhen = (from: number, targetHours: number): number => {
    if (targetHours <= 0) return from;
    let lo = from;
    let hi = from + targetHours * 4 * HOUR + HOUR;
    for (let i = 0; i < 64; i++) {
      const mid = (lo + hi) / 2;
      if (between(from, mid) < targetHours) lo = mid;
      else hi = mid;
    }
    return hi;
  };

  return { between, timeWhen };
}

/** The effective hours that pass between two real instants (milliseconds), given when the app was open. */
export function effectiveHoursBetween(from: number, to: number, sessions: readonly Session[]): number {
  return makeEffectiveClock(sessions).between(from, to);
}

/** The real instant at which `targetHours` effective hours have passed since `from`. */
export function timeWhenEffectiveReaches(from: number, targetHours: number, sessions: readonly Session[]): number {
  return makeEffectiveClock(sessions).timeWhen(from, targetHours);
}
